'use client';

import { useActiveWallet, useWallets } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/app-popover';
import { Icon } from '@/components/ui/icon';
import {
  ArrowDataTransferVerticalIcon,
  ArrowDown01Icon,
  Copy01Icon,
  Tick02Icon,
  Wallet01Icon,
} from '@/components/ui/icon/glyphs';
import { walletDisplayLabel } from '@/components/wallet/wallet-display-label';
import { useFormattedCurrency } from '@/lib/currency';
import { cn } from '@/lib/ui/utils';
import { copyWalletAddress } from '@/lib/wallet/copy-address';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
import { useWalletBalances } from '@/queries/wallet-balance';
import type { Wallet } from '@/types/auth';

// ---------------------------------------------------------------------------
// Picker row (one per wallet in the dropdown)
// ---------------------------------------------------------------------------

function WalletPickerRow({
  wallet,
  isSelected,
  onSelect,
}: {
  wallet: Wallet;
  isSelected: boolean;
  onSelect: () => void;
}): React.ReactNode {
  const tTable = useTranslations('wallet.table');
  const { formatBalance } = useFormattedCurrency();
  const { perWallet } = useWalletBalances([wallet.address]);
  const balance = perWallet.get(wallet.address) ?? 0;

  return (
    <div className="flex items-center">
      <button
        type="button"
        onClick={onSelect}
        className="flex flex-1 items-center gap-2 rounded-overlay-item px-2 py-1.5 text-left text-body transition-colors hover:bg-background-tertiary"
      >
        <Icon
          icon={Tick02Icon}
          className={cn('size-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')}
          aria-hidden="true"
        />
        <span className="truncate">{walletDisplayLabel(wallet)}</span>
        <span className="shrink-0 text-caption text-foreground-secondary">
          {formatBalance(balance)}
        </span>
      </button>
      <button
        type="button"
        className="shrink-0 rounded p-1.5 text-foreground-secondary hover:text-foreground-primary"
        onClick={(): void => {
          copyWalletAddress(wallet.address, walletDisplayLabel(wallet));
        }}
        aria-label={tTable('copy_address')}
      >
        <Icon icon={Copy01Icon} className="size-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface WalletPickerDropdownProps {
  /** Currently selected wallet ID. */
  selectedWalletId: string;
  /** Called when the user picks a different wallet. */
  onWalletChange: (walletId: string) => void;
  /** Popover alignment. */
  align?: 'start' | 'center' | 'end';
  /** Button size variant. */
  size?: 'default' | 'sm';
  /** Override className merged onto the trigger button. Use for callers that want the
   *  trigger to look like an input shell (transfer/move funds), not a secondary button. */
  triggerClassName?: string;
  /** Flat wallet/balance row used in detail-page transaction forms. */
  triggerVariant?: 'button' | 'detail-row';
  /** Selected balance rendered by the caller's authoritative form query. */
  selectedValue?: string;
  /** Label shown before a wallet is connected. */
  fallbackLabel?: string;
  /** Prevent switching while the caller is submitting. */
  disabled?: boolean;
  /** State color for the selected balance, such as insufficient funds. */
  valueClassName?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Reusable wallet picker dropdown. Shows the selected wallet with balance in a
 * trigger button; opens a popover with all active wallets when clicked.
 *
 * If only one wallet exists, renders a static label instead of a dropdown.
 */
export function WalletPickerDropdown({
  selectedWalletId,
  onWalletChange,
  align = 'start',
  size = 'default',
  triggerClassName,
  triggerVariant = 'button',
  selectedValue,
  fallbackLabel = '',
  disabled = false,
  valueClassName,
}: WalletPickerDropdownProps): React.ReactNode {
  const { wallets } = useWallets();
  const { enabled: multiWalletEnabled, primaryWallet } = useMultiWalletFeature();
  const { formatBalance } = useFormattedCurrency();
  const [open, setOpen] = useState<boolean>(false);

  const scopedWallets = multiWalletEnabled
    ? wallets
    : primaryWallet === null
      ? []
      : [primaryWallet];
  const activeWallets = scopedWallets.filter(
    (wallet) => wallet.archivedAt === null && wallet.isAvailable !== false,
  );
  const selectedWallet =
    activeWallets.find((wallet) => wallet.id === selectedWalletId) ?? activeWallets[0];
  const selectedAddress = selectedWallet?.address;
  // A caller-supplied value is authoritative; reading the balance again would cost an RPC call.
  const { perWallet: selectedPerWallet } = useWalletBalances(
    selectedAddress !== undefined && selectedValue === undefined ? [selectedAddress] : [],
  );
  const selectedBalance =
    selectedAddress !== undefined ? (selectedPerWallet.get(selectedAddress) ?? 0) : 0;
  const selectedBalanceLabel = selectedValue ?? formatBalance(selectedBalance);

  if (triggerVariant === 'detail-row' && activeWallets.length <= 1) {
    return (
      <div className="flex min-h-6 w-full items-center justify-between pl-1 text-body">
        <DetailWalletPickerRow
          label={
            selectedWallet !== undefined && multiWalletEnabled
              ? walletDisplayLabel(selectedWallet)
              : fallbackLabel
          }
          value={selectedWallet ? selectedBalanceLabel : formatBalance(0)}
          {...(valueClassName === undefined ? {} : { valueClassName })}
        />
      </div>
    );
  }

  if (activeWallets.length <= 1 && selectedWallet !== undefined) {
    return (
      <span className="flex items-center gap-1.5 text-body text-foreground-secondary">
        <span className="truncate">{walletDisplayLabel(selectedWallet)}</span>
        <span className="shrink-0 text-caption">{selectedBalanceLabel}</span>
      </span>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {triggerVariant === 'detail-row' ? (
          <button
            type="button"
            disabled={disabled}
            aria-expanded={open}
            className={cn(
              'flex min-h-6 w-full items-center justify-between rounded-control pl-1 text-body transition-colors enabled:hover:text-foreground-primary disabled:cursor-not-allowed disabled:opacity-50',
              triggerClassName,
            )}
          >
            <DetailWalletPickerRow
              label={selectedWallet ? walletDisplayLabel(selectedWallet) : fallbackLabel}
              value={selectedWallet ? selectedBalanceLabel : formatBalance(0)}
              showCaret
              {...(valueClassName === undefined ? {} : { valueClassName })}
            />
          </button>
        ) : (
          <AnimatedButton
            type="button"
            variant="secondary"
            size={size}
            disabled={disabled}
            aria-expanded={open}
            className={cn('w-full justify-between font-normal', triggerClassName)}
          >
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate">
                {selectedWallet ? walletDisplayLabel(selectedWallet) : ''}
              </span>
              <span className="shrink-0 text-caption text-foreground-secondary">
                {selectedBalanceLabel}
              </span>
            </span>
            <Icon
              icon={ArrowDataTransferVerticalIcon}
              className="size-3.5 shrink-0 opacity-50"
              aria-hidden="true"
            />
          </AnimatedButton>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="min-w-[var(--radix-popover-trigger-width)] w-auto p-1"
        align={align}
      >
        {activeWallets.map((w) => (
          <WalletPickerRow
            key={w.id}
            wallet={w}
            isSelected={selectedWallet?.id === w.id}
            onSelect={(): void => {
              onWalletChange(w.id);
              setOpen(false);
            }}
          />
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function DetailWalletPickerRow({
  label,
  value,
  showCaret = false,
  valueClassName,
}: {
  label: string;
  value: string;
  showCaret?: boolean;
  valueClassName?: string;
}): React.ReactNode {
  return (
    <>
      <span className="flex min-w-0 items-center gap-2 text-foreground-secondary">
        <Icon icon={Wallet01Icon} className="size-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">{label}</span>
      </span>
      <span className="flex shrink-0 items-center gap-1.5">
        <span className={cn('font-medium tabular-nums text-foreground-primary', valueClassName)}>
          {value}
        </span>
        {showCaret && (
          <Icon
            icon={ArrowDown01Icon}
            className="size-3.5 text-foreground-tertiary"
            aria-hidden="true"
          />
        )}
      </span>
    </>
  );
}

// ---------------------------------------------------------------------------
// Hook — manages wallet selection state with sensible defaults
// ---------------------------------------------------------------------------

interface UseWalletPickerResult {
  selectedWalletId: string;
  setSelectedWalletId: (id: string) => void;
  selectedWallet: Wallet | undefined;
}

/**
 * Manages wallet selection state. Defaults to the active wallet, resets when
 * `resetKey` changes (e.g. dialog open state).
 */
export function useWalletPicker(resetKey: boolean): UseWalletPickerResult {
  const { activeWallet } = useActiveWallet();
  const { wallets } = useWallets();
  const { enabled: multiWalletEnabled, primaryWallet } = useMultiWalletFeature();
  const [selectedWalletId, setSelectedWalletId] = useState<string>('');
  const scopedWallets = multiWalletEnabled
    ? wallets
    : primaryWallet === null
      ? []
      : [primaryWallet];
  const firstAvailableWallet = scopedWallets.find(
    (wallet) => wallet.archivedAt === null && wallet.isAvailable !== false,
  );
  const effectiveActiveWallet = multiWalletEnabled ? activeWallet : firstAvailableWallet;

  useEffect(() => {
    if (resetKey) {
      setSelectedWalletId(effectiveActiveWallet?.id ?? firstAvailableWallet?.id ?? '');
    }
  }, [resetKey, effectiveActiveWallet?.id, firstAvailableWallet?.id]);

  const selectedWallet: Wallet | undefined =
    scopedWallets.find(
      (wallet) =>
        wallet.id === selectedWalletId &&
        wallet.archivedAt === null &&
        wallet.isAvailable !== false,
    ) ??
    effectiveActiveWallet ??
    firstAvailableWallet;

  return { selectedWalletId, setSelectedWalletId, selectedWallet };
}
